import React, { useMemo, useState, useEffect } from 'react'
import { useSelector } from 'react-redux'

import debounce from 'lodash/debounce'
import { IconButton, makeStyles, TextField, Popper, useTheme } from '@material-ui/core'
import Autocomplete from '@material-ui/lab/Autocomplete'
import LocationOnIcon from '@material-ui/icons/LocationOn'
import { Close } from '@material-ui/icons'

import AddressOption from './AddressOption'
import useViewportSizes from '~/components/useViewportSize'
import { useLiterals } from '~/components/Literals'
import track from '~/track'
import api from '~/api'

function getAddress(data, type, size) {
  return data.filter(({ types }) => types.find((el) => type.includes(el)))?.[0]?.[size] || ''
}

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%'
  },
  inputIcon: {
    color: theme.palette.primary.main,
    fontSize: 14
  },
  innerInput: {
    padding: 10
  },
  paperContent: ({ isMobile }) => ({
    maxHeight: !isMobile && 200,
    overflowY: 'auto',
    padding: 0,
    margin: 0,
    '& li': {
      paddingBlock: 8,
      borderBottom: '1px solid #F5F5F5'
    },
    ...theme.gofind.scrollBar
  }),
  option: ({ isMobile }) =>
    isMobile
      ? {
          paddingLeft: 0
        }
      : {}
}))

function CustomPopper(props) {
  return <Popper {...props} container={document.getElementById('gofind-finder') || document.body} />
}

export default function SearchAddress({
  onChangeValue,
  onChangeLocation,
  label,
  placeholder,
  size
}) {
  const theme = useTheme()
  const viewport = useViewportSizes()
  const isMobile = viewport.width < theme.gofind.desktopMaxWidth

  const classes = useStyles({ isMobile })
  const config = useSelector((state) => state.config)

  const [inputValue, setInputValue] = useState('')
  const [options, setOptions] = useState([])
  const [isNoOptions, setIsNoOptions] = useState(false)
  const [loading, setLoading] = useState(false)

  const { getLiteral } = useLiterals()

  const promiseOptions = (value) =>
    api
      .searchLocation(value, config)
      .then((res) => res.map((item) => ({ ...item, label: api.getLabel(item, getLiteral) })))

  const handleSearch = useMemo(
    () =>
      debounce((q) => {
        setIsNoOptions(false)

        if (!q) {
          setLoading(false)

          return undefined
        }

        if (q.length >= 5) {
          setLoading(true)
          promiseOptions(q).then((result) => {
            if (result.length === 0) {
              api.fetchGeoCodeGoogle(q).then(({ results = [] }) => {
                results = results.map(({ formatted_address, place_id, address_components }) => ({
                  label: formatted_address,
                  locationId: place_id,
                  address: {
                    country: getAddress(address_components, 'country', 'long_name'),
                    state: getAddress(
                      address_components,
                      'administrative_area_level_1',
                      'short_name'
                    ),
                    city: getAddress(
                      address_components,
                      ['locality', 'administrative_area_level_2'],
                      'long_name'
                    ),
                    postalCode: getAddress(address_components, 'postal_code', 'long_name')
                  }
                }))

                setLoading(false)
                setIsNoOptions(true)
                setOptions(results)
              })
            } else {
              setLoading(false)
              setIsNoOptions(false)
              setOptions(result)
            }
          })
        }
      }, 1000),
    []
  )

  const handleClear = () => {
    setOptions([])
    setLoading(false)
    setInputValue('')
  }

  const handleInputChange = (e, item, reason) => {
    switch (reason) {
      case 'clear':
        handleClear()
        break
      case 'input':
        if (e.key === 'Enter') e.stopPropagation()

        setInputValue(item)
        break
    }
  }

  const handleSelect = async (e, item, reason) => {
    if (item && reason === 'select-option') {
      try {
        const Response = await api.fetchGeoCode(item)

        const location = {
          ...item.address,
          lat: Response.position?.lat || Response.items[0].position.lat,
          lng: Response.position?.lng || Response.items[0].position.lng,
          description: item.label
        }

        track('escolherLocalizacao', { name: item.label, category: 'buscar' })

        onChangeLocation(location)
        setInputValue('')
      } catch (error) {
        console.warn('cannot change location', error.message)
      }
    }
  }

  useEffect(() => handleSearch(inputValue), [inputValue])

  useEffect(() => {
    if (!inputValue) {
      setOptions([])
      onChangeValue?.(false)
    }

    if (inputValue) onChangeValue?.(true)
  }, [inputValue])

  return (
    <Autocomplete
      open={inputValue.length >= 5}
      autoFocus={false}
      autoComplete
      clearOnBlur
      className={classes.root}
      classes={{ paper: classes.paper, option: classes.option }}
      getOptionLabel={(option) => (typeof option === 'string' ? option : option.label)}
      filterOptions={(x) => x}
      options={options}
      onChange={handleSelect}
      onInputChange={handleInputChange}
      inputValue={inputValue}
      renderInput={(params) => (
        <TextField
          {...params}
          autoFocus={false}
          size={size ? size : 'medium'}
          label={label}
          placeholder={placeholder}
          color="primary"
          variant="outlined"
          fullWidth
          focused={false}
          InputProps={{
            ...params.InputProps,
            style: { padding: 10 },
            startAdornment: <LocationOnIcon className={classes.inputIcon} />,
            endAdornment: (
              <div>
                {inputValue.length ? (
                  <IconButton size="small" onClick={handleClear}>
                    <Close fontSize="small" />
                  </IconButton>
                ) : null}
              </div>
            )
          }}
        />
      )}
      ListboxProps={{ className: classes.paperContent }}
      PopperComponent={CustomPopper}
      renderOption={(option) => <AddressOption option={option} />}
      loading={loading}
      loadingText={`${getLiteral('1171')}...`}
      noOptionsText={
        isNoOptions && inputValue.length >= 5 ? getLiteral('2526') : `${getLiteral('1171')}...`
      }
    />
  )
}
